import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import type { ContactRequest, FAQCategory } from '../types';

interface ContactFormData {
  category_id: string | null;
  subject?: string;
  message: string;
  first_name: string;
  last_name: string;
  // For guest users (unauthenticated)
  guest_email?: string;
}

export function useContactForm() {
  const { user } = useAuth();

  const submitMutation = useMutation({
    mutationFn: async (data: ContactFormData) => {
      // Determine if this is an authenticated or guest submission
      const isAuthenticated = !!user;

      const insertData = isAuthenticated
        ? {
            user_id: user.id,
            category_id: data.category_id,
            first_name: data.first_name,
            last_name: data.last_name,
            email: user.email || '',
            subject: data.subject || null,
            message: data.message,
            status: 'pending',
            is_authenticated: true,
          }
        : {
            user_id: null,
            category_id: data.category_id,
            guest_name: `${data.first_name} ${data.last_name}`.trim(),
            guest_email: data.guest_email,
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.guest_email || '',
            subject: data.subject || null,
            message: data.message,
            status: 'pending',
            is_authenticated: false,
          };

      const { data: result, error } = await supabase
        .from('contact_requests')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return result as ContactRequest;
    },
  });

  return {
    submit: submitMutation.mutateAsync,
    isSubmitting: submitMutation.isPending,
    isSuccess: submitMutation.isSuccess,
    error: submitMutation.error,
    reset: submitMutation.reset,
  };
}

// Hook to fetch user profile data
export function useUserProfile() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

// Fetch user's previous contact requests
export function useMyContactRequests() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['my-contact-requests', user?.id],
    queryFn: async (): Promise<ContactRequest[]> => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('contact_requests')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });
}

// Fetch categories for dropdown (including "Sonstiges" option)
export function useContactCategories() {
  return useQuery({
    queryKey: ['contact-categories'],
    queryFn: async (): Promise<FAQCategory[]> => {
      const { data, error } = await supabase
        .from('faq_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      return data || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Generate simple math captcha
export function generateMathCaptcha(locale: 'de' | 'en' = 'de'): { question: string; answer: number } {
  const operations = ['+', '-', '*'];
  const operation = operations[Math.floor(Math.random() * operations.length)];

  let a: number, b: number, answer: number;

  switch (operation) {
    case '+':
      a = Math.floor(Math.random() * 20) + 1;
      b = Math.floor(Math.random() * 20) + 1;
      answer = a + b;
      break;
    case '-':
      a = Math.floor(Math.random() * 20) + 10;
      b = Math.floor(Math.random() * 10) + 1;
      answer = a - b;
      break;
    case '*':
      a = Math.floor(Math.random() * 10) + 1;
      b = Math.floor(Math.random() * 10) + 1;
      answer = a * b;
      break;
    default:
      a = 5;
      b = 3;
      answer = 8;
  }

  const operationSymbol = operation === '*' ? '×' : operation;
  const question = locale === 'en'
    ? `What is ${a} ${operationSymbol} ${b}?`
    : `Was ergibt ${a} ${operationSymbol} ${b}?`;
  return { question, answer };
}
